import React, { useEffect, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import defaultAdImage from "../assets/images/sash.webp";
import EnquiryModal from "./EnquiryModal";
import LazyImage from "./LazyImage";

const CERTS = [
  { src: "/ISO9001-2015.png",    alt: "ISO 9001:2015",    label: "ISO 9001:2015 Quality Management Systems" },
  { src: "/ISO27001-2022.png",   alt: "ISO 27001:2022",   label: "ISO 27001:2022 Information Security Management Systems" },
  { src: "/csa.png",             alt: "CSA",              label: "CSA- Tier 1 Managed Service Provider" },
  { src: "/DATA PROTECTION.png", alt: "Data Protection",  label: "DPC Certified & Data Protection Act, 2012 (Act 843) Compliant" },
  { src: "/nita.png",            alt: "NITA",             label: "NITA - IT Service Provider" },
];

const DEFAULT_HERO = {
  Title: "Stay Relevant.",
  Subtitle: "Simplify Your Business with Smart Technology.",
  Subtitle2:
    "ECL makes it easy with the most efficient technology for all your business processes.",
};

const Hero = () => {
  const [hero] = useState(DEFAULT_HERO);
  const [adImages, setAdImages] = useState([]);
  const [currentAdImage, setCurrentAdImage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    async function loadHero() {
      try {
        const [, adRes] = await Promise.all([
          fetchFromStrapi("/ecl-hero-sections"),
          fetchFromStrapi("/ecl-ad-sections"),
        ]);
        const adItems = adRes?.data || [];
        const urls = adItems
          .map(item => item?.attributes?.Image?.data?.attributes?.url)
          .filter(Boolean);
        if (urls.length) setAdImages(urls);
        console.log("Loaded hero and ad data:", { adImages: urls });
      } catch (err) {
        console.error("Error loading hero:", err);
      }
    }
    loadHero();
  }, []);

  useEffect(() => {
    if (adImages.length <= 1) return;
    const id = setInterval(
      () => setCurrentAdImage(i => (i + 1) % adImages.length),
      5000
    );
    return () => clearInterval(id);
  }, [adImages.length]);

  const currentAdImgUrl = adImages[currentAdImage];
  const isVideo = url => typeof url === "string" && /\.(mp4|webm|ogg)$/i.test(url);

  return (
    <div>
      <section className="hero-section position-relative overflow-hidden">
        <video className="hero-video" autoPlay muted loop playsInline poster="/hero-poster.jpg">
          <source src="/hero.mp4" type="video/mp4" />
        </video>

        <div className="hero-overlay" />

        <div className="container hero-content">
            <div className="row align-items-center" style={{ paddingBottom: 48 }}>
            {/* Hero text */}
            <div className="col-lg-7">
              <h1 className="fw-bold display-3 lh-tight mb-3" style={{ lineHeight: "0.5" }}>
                <span className="hero-text hero-text-blue">{hero.Title}</span>
                <br />
                <span className="hero-text text-light">{hero.Subtitle}</span>
              </h1>
              <p className="lead text-light mb-4">{hero.Subtitle2}</p>

              <button
                className="hero-btn btn btn-lg px-5 shadow-sm mb-4"
                style={{ backgroundColor: "#00ddff", color: "white", borderRadius: 4, marginTop: "20px" }}
                onClick={() => setModalOpen(true)}
              >
                Talk to us
              </button>
            </div>

            {/* Certifications marquee — mobile only */}
            <div className="col-12 d-lg-none mt-4">
              <p style={{
                color: "rgba(255,255,255,0.45)",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                marginBottom: 12,
              }}>
                Certified &amp; trusted
              </p>
              <div className="carousel-wrapper" style={{ overflow: "hidden" }}>
                <div className="carousel-track">
                  {[...CERTS, ...CERTS].map((cert, i) => (
                    <img
                      key={i}
                      src={cert.src}
                      alt={cert.alt}
                      loading="lazy"
                      decoding="async"
                      style={{ height: 52, width: 56, objectFit: "contain", flexShrink: 0 }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Certifications card */}
            <div className="col-lg-5 d-none d-lg-flex justify-content-end">
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 12,
                padding: "24px 28px",
                backdropFilter: "blur(10px)",
                minWidth: 88,
                width: "100%",
                maxWidth: 360,
              }}>
                <p style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  margin: "0 0 10px",
                  alignSelf: "flex-start",
                }}>
                  Certified &amp; trusted
                </p>
                {CERTS.map((cert, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 24, width: "100%" }}>
                    <img
                      src={cert.src}
                      alt={cert.alt}
                      style={{ height: 72, width: 78, objectFit: "contain", flexShrink: 0, marginBottom:'5px' }}
                    />
                    <span style={{
                      color: "rgba(255,255,255,0.75)",
                      fontSize: "0.72rem",
                      lineHeight: 1.35,
                      letterSpacing: "0.02em",
                    }}>
                      {cert.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ads banner */}
      <section
        className="position-relative"
        style={{ zIndex: 3, marginTop: 50, left: 0, right: 0 }}
      >
        <div className="container-xxl">
          {/* Desktop — cycles through all Strapi ad images/videos */}
          <div className="d-none d-md-block" style={{ position: "relative", borderRadius: 16, overflow: "hidden" }}>
            {isVideo(currentAdImgUrl) ? (
              <video
                key={currentAdImage}
                autoPlay
                muted
                loop
                playsInline
                className="shadow-sm"
                style={{ display: "block", width: "100%", height: "auto" }}
              >
                <source src={currentAdImgUrl} type="video/mp4" />
              </video>
            ) : (
              <LazyImage
                key={currentAdImage}
                src={currentAdImgUrl || defaultAdImage}
                alt="ECL Ad Banner"
                objectFit="contain"
                className="img-fluid shadow-sm w-100"
                style={{ borderRadius: 0, display: "block" }}
              />
            )}
            {/* Dot indicators — only shown when there are multiple images */}
            {adImages.length > 1 && (
              <div style={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
              }}>
                {adImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentAdImage(i)}
                    aria-label={`Ad ${i + 1}`}
                    style={{
                      width: i === currentAdImage ? 20 : 8,
                      height: 8,
                      borderRadius: 4,
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      background: i === currentAdImage ? "#009EDC" : "rgba(255,255,255,0.6)",
                      transition: "width 1.5s ease, background 1.5s ease",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Mobile */}
          <LazyImage
            src="/adBannerMini.png"
            alt="ECL Ad Banner"
            objectFit="cover"
            className="img-fluid shadow-sm w-100 d-md-none"
            style={{ borderRadius: 16 }}
          />
        </div>
      </section>

      <EnquiryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Hero;
